import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Box, Divider, IconButton, Typography } from "@mui/joy";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
}

const EMPTY_HTML = "<p></p>";

export function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            Placeholder.configure({ placeholder: placeholder ?? "" }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html === EMPTY_HTML ? "" : html);
        },
    });

    // Keeps the editor in sync when `value` changes from outside (e.g. after
    // fetching the experience), without fighting the user's own typing.
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const normalized = value || "";
        if (normalized !== current && !(normalized === "" && current === EMPTY_HTML)) {
            editor.commands.setContent(normalized, { emitUpdate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) return null;

    const setLink = () => {
        const previousUrl = editor.getAttributes("link").href as string | undefined;
        const url = window.prompt("URL del link", previousUrl || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const toolBtnSx = (active: boolean) => ({
        color: active ? "#fff" : "neutral.700",
        backgroundColor: active ? "primary.500" : "transparent",
        "&:hover": { backgroundColor: active ? "primary.600" : "neutral.200" },
    });

    return (
        <Box
            sx={{
                border: "1px solid",
                borderColor: "neutral.outlinedBorder",
                borderRadius: "sm",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.25,
                    p: 0.5,
                    borderBottom: "1px solid",
                    borderColor: "neutral.outlinedBorder",
                    backgroundColor: "background.level1",
                }}
            >
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("heading", { level: 2 }))}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                    <Typography level="body-sm" fontWeight="xl">H2</Typography>
                </IconButton>
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("heading", { level: 3 }))}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                    <Typography level="body-sm" fontWeight="xl">H3</Typography>
                </IconButton>
                <Divider orientation="vertical" sx={{ mx: 0.5 }} />
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("bold"))}
                    onClick={() => editor.chain().focus().toggleBold().run()}>
                    <FormatBoldIcon fontSize="small" />
                </IconButton>
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("italic"))}
                    onClick={() => editor.chain().focus().toggleItalic().run()}>
                    <FormatItalicIcon fontSize="small" />
                </IconButton>
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("underline"))}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}>
                    <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
                <Divider orientation="vertical" sx={{ mx: 0.5 }} />
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("bulletList"))}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}>
                    <FormatListBulletedIcon fontSize="small" />
                </IconButton>
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("orderedList"))}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                    <FormatListNumberedIcon fontSize="small" />
                </IconButton>
                <Divider orientation="vertical" sx={{ mx: 0.5 }} />
                <IconButton size="sm" variant="plain" sx={toolBtnSx(editor.isActive("link"))} onClick={setLink}>
                    <LinkIcon fontSize="small" />
                </IconButton>
                <IconButton size="sm" variant="plain"
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
                    <FormatClearIcon fontSize="small" />
                </IconButton>
                <Divider orientation="vertical" sx={{ mx: 0.5 }} />
                <IconButton size="sm" variant="plain" disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}>
                    <UndoIcon fontSize="small" />
                </IconButton>
                <IconButton size="sm" variant="plain" disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}>
                    <RedoIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box
                sx={{
                    p: 1.5,
                    minHeight: 180,
                    fontSize: 14,
                    cursor: "text",
                    "& .ProseMirror": { outline: "none" },
                    "& .ProseMirror p.is-editor-empty:first-of-type::before": {
                        content: "attr(data-placeholder)",
                        color: "var(--joy-palette-neutral-400)",
                        float: "left",
                        height: 0,
                        pointerEvents: "none",
                    },
                    "& ul, & ol": { paddingLeft: "1.25rem", margin: "0.25rem 0" },
                    "& p": { margin: "0.25rem 0" },
                    "& a": { color: "#0f5ac0" },
                }}
                onClick={() => editor.chain().focus().run()}
            >
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
}
